import * as React from 'react';
import { cn } from '@ooh/ui/lib/utils';
import { Button } from '@ooh/ui/components/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@ooh/ui/components/table';
import type { AmbiguousSupplierEntry } from '@ooh/contracts';

// Re-exported so callers can import the canonical @ooh/contracts type from this
// component module — keeps a single import source for both the component and its props.
export type { AmbiguousSupplierEntry };

export interface SupplierDedupPhaseProps {
  ambiguousSuppliers: AmbiguousSupplierEntry[];
  onNext: () => void;
  labels: {
    heading: string;
    subheading: string;
    useExistingLabel: string;
    /** Suffix rendered after the similarity percentage, e.g. "zgodności" -> "89% zgodności". */
    similaritySuffix: string;
    createNewLabel: string;
    nextButton: string;
    submitting: string;
    errorGeneric: string;
    columnFileName: string;
    columnAction: string;
    columnSuggestions: string;
    allDecidedHint: string;
  };
  onSaveResolutions: (resolutions: Record<string, string | null>) => Promise<void>;
  isSaving: boolean;
  errorMessage?: string | null;
}

type Decision = string | null | 'undecided';

export function SupplierDedupPhase({
  ambiguousSuppliers,
  onNext,
  labels,
  onSaveResolutions,
  isSaving,
  errorMessage,
}: SupplierDedupPhaseProps) {
  // Decision map: name → existing supplier name | null (create new) | 'undecided'.
  // Uses Map (not a plain object) so a supplier literally named '__proto__', 'constructor',
  // or 'toString' can never collide with an inherited Object.prototype member.
  const [decisions, setDecisions] = React.useState<Map<string, Decision>>(
    () => new Map(ambiguousSuppliers.map((entry) => [entry.name, 'undecided' as Decision])),
  );

  const allDecided = React.useMemo(
    () => Array.from(decisions.values()).every((d) => d !== 'undecided'),
    [decisions],
  );

  function setDecision(name: string, decision: Decision) {
    setDecisions((prev) => new Map(prev).set(name, decision));
  }

  async function handleNext() {
    const resolutions: Record<string, string | null> = {};
    for (const [name, decision] of decisions) {
      resolutions[name] = decision === 'undecided' ? null : decision;
    }
    try {
      await onSaveResolutions(resolutions);
      onNext();
    } catch {
      // Save failure is already reflected reactively via the errorMessage prop
      // (driven by the parent's mutation isError state) — swallow here so this
      // doesn't surface as an unhandled promise rejection. The user stays on
      // this phase and sees the error banner.
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="phase-supplier-dedup">
      <div>
        <h2 className="text-base font-semibold">{labels.heading}</h2>
        <p className="text-sm text-muted-foreground mt-1">{labels.subheading}</p>
      </div>

      {errorMessage ? (
        <div
          className="rounded-md border border-destructive bg-destructive/10 px-4 py-3 text-sm text-destructive"
          data-testid="dedup-error"
        >
          {errorMessage}
        </div>
      ) : null}

      <div className="rounded-md border border-border overflow-hidden">
        <Table className="text-sm">
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>{labels.columnFileName}</TableHead>
              <TableHead>{labels.columnSuggestions}</TableHead>
              <TableHead>{labels.columnAction}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ambiguousSuppliers.map((entry) => (
              <TableRow
                key={entry.name}
                data-testid={`dedup-supplier-row-${entry.name}`}
                className={cn(
                  decisions.get(entry.name) === 'undecided' &&
                    'bg-amber-50 border-l-2 border-l-amber-400',
                )}
              >
                <TableCell className="font-medium">{entry.name}</TableCell>
                <TableCell>
                  <div className="space-y-1">
                    {entry.suggestions.map((s) => (
                      <Button
                        key={s.name}
                        type="button"
                        variant="outline"
                        size="xs"
                        data-testid={`dedup-existing-option-${s.name}`}
                        onClick={() => setDecision(entry.name, s.name)}
                        className={cn(
                          'w-full justify-start gap-2 text-left',
                          decisions.get(entry.name) === s.name &&
                            'bg-primary/10 text-primary font-medium ring-1 ring-primary/30',
                        )}
                      >
                        <span className="text-muted-foreground">{labels.useExistingLabel}:</span>
                        <span className="font-medium">{s.name}</span>
                        {/* `score` is a DISTANCE (0.00 = identical) — invert it for display
                            so users read a high percentage as a strong match (CU-86can6563;
                            a raw "11%" for a near-identical name read as low confidence). */}
                        <span className="text-muted-foreground text-xs">
                          ({100 - Math.round(s.score * 100)}% {labels.similaritySuffix})
                        </span>
                      </Button>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="secondary"
                    size="xs"
                    data-testid={`dedup-create-new-${entry.name}`}
                    onClick={() => setDecision(entry.name, null)}
                    className={cn(
                      decisions.get(entry.name) === null &&
                        'bg-primary/10 text-primary font-medium border-primary/30',
                    )}
                  >
                    {labels.createNewLabel}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col items-end gap-1.5 pt-2">
        <Button
          type="button"
          data-testid="dedup-next-button"
          disabled={!allDecided || isSaving}
          onClick={() => void handleNext()}
        >
          {isSaving ? labels.submitting : labels.nextButton}
        </Button>
        {!allDecided ? (
          <p className="text-xs text-muted-foreground" data-testid="dedup-all-decided-hint">
            {labels.allDecidedHint}
          </p>
        ) : null}
      </div>
    </div>
  );
}
